import React, { useMemo } from "react";
import styles from "../../styles.module.css";

/**
 * CustomPagination
 * Props:
 *  - page: 0-based current page index
 *  - rowsPerPage: rows shown per page
 *  - totalCount: total number of records
 *  - onPageChange: (newPage) => void  (0-based)
 */
const CustomPagination = ({ page, rowsPerPage, totalCount, onPageChange }) => {
  const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage));
  const currentPage = page + 1; // convert to 1-based for display

  const startRecord = totalCount === 0 ? 0 : page * rowsPerPage + 1;
  const endRecord = totalCount === 0 ? 0 : Math.min((page + 1) * rowsPerPage, totalCount);

  // Build page number list with ellipsis
  const pageNumbers = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages = [];
    if (currentPage <= 4) {
      pages.push(1, 2, 3, "...", totalPages);
    } else if (currentPage >= totalPages - 3) {
      pages.push(1, "...", totalPages - 2, totalPages - 1, totalPages);
    } else {
      pages.push(1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages);
    }
    return pages;
  }, [currentPage, totalPages]);

  const handleClick = (p) => {
    if (p !== "..." && p !== currentPage) {
      onPageChange(p - 1); // back to 0-based
    }
  };

  return (
    <div className={styles.pagination_wrapper}>
      <span className={styles.pagination_info}>
        Showing {startRecord} to {endRecord} of {totalCount} records
      </span>

      <div className={styles.pagination_controls}>
        {/* Previous */}
        <button
          className={`${styles.page_btn} ${styles.page_nav_btn}`}
          onClick={() => onPageChange(page - 1)}
          disabled={page === 0}
          aria-label="Previous page"
        >
          ‹
        </button>

        {/* Page numbers */}
        {pageNumbers.map((p, idx) =>
          p === "..." ? (
            <span key={`ellipsis-${idx}`} className={styles.page_ellipsis}>
              ...
            </span>
          ) : (
            <button
              key={p}
              className={`${styles.page_btn} ${p === currentPage ? styles.page_btn_active : ""}`}
              onClick={() => handleClick(p)}
              aria-label={`Page ${p}`}
              aria-current={p === currentPage ? "page" : undefined}
            >
              {p}
            </button>
          )
        )}

        {/* Next */}
        <button
          className={`${styles.page_btn} ${styles.page_nav_btn}`}
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages - 1}
          aria-label="Next page"
        >
          ›
        </button>
      </div>
    </div>
  );
};

export default CustomPagination;
